"use client";

/**
 * DrivePickerModal — tree-browse the user's Google Drive, pick one or more
 * folders, attach to an agent.
 *
 * MVP scope:
 *   - Navigate into folders (breadcrumbs with back to root)
 *   - Multi-select folders (not files — files are a Phase 2 attach kind)
 *   - Confirm → POST /api/integrations/:id/attachments
 *
 * Not in scope:
 *   - Search within picker (use Drive web UI for now)
 *   - Pagination beyond 100 items (rare for a user's Drive root)
 */

import { useCallback, useEffect, useState } from "react";
import { ChevronRight, Folder, File as FileIcon, X, Check } from "lucide-react";
import { api } from "@/lib/api";

interface DriveItem {
  id: string;
  name: string;
  mime_type: string;
  is_folder: boolean;
  modified_time: string | null;
  size_bytes: number | null;
}

interface DrivePickerModalProps {
  integrationId: string;
  agentId: string;
  agentName?: string;
  onClose: () => void;
  onAttached?: (count: number) => void;
}

interface Breadcrumb {
  id: string;
  name: string;
}

const ROOT: Breadcrumb = { id: "root", name: "My Drive" };

export function DrivePickerModal({
  integrationId,
  agentId,
  agentName,
  onClose,
  onAttached,
}: DrivePickerModalProps) {
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([ROOT]);
  const [items, setItems] = useState<DriveItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Map<string, DriveItem>>(new Map());
  const [attaching, setAttaching] = useState(false);

  const currentFolder = breadcrumbs[breadcrumbs.length - 1];

  const loadChildren = useCallback(
    async (parentId: string) => {
      setLoading(true);
      setError(null);
      try {
        const result = await api.integrations.driveChildren(integrationId, parentId);
        setItems(result.items);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : "Failed to load folder";
        setError(msg);
      } finally {
        setLoading(false);
      }
    },
    [integrationId],
  );

  useEffect(() => {
    void loadChildren(currentFolder.id);
  }, [currentFolder.id, loadChildren]);

  const openFolder = useCallback((item: DriveItem) => {
    if (!item.is_folder) return;
    setBreadcrumbs(prev => [...prev, { id: item.id, name: item.name }]);
  }, []);

  const navTo = useCallback((index: number) => {
    setBreadcrumbs(prev => prev.slice(0, index + 1));
  }, []);

  const toggleSelect = useCallback((item: DriveItem) => {
    if (!item.is_folder) return;
    setSelected(prev => {
      const next = new Map(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.set(item.id, item);
      return next;
    });
  }, []);

  const handleAttach = useCallback(async () => {
    if (selected.size === 0) return;
    setAttaching(true);
    setError(null);
    try {
      const resourceIds = Array.from(selected.keys());
      const resourceLabels: Record<string, string> = {};
      for (const [id, item] of selected.entries()) resourceLabels[id] = item.name;
      await api.integrations.attach(integrationId, {
        agent_id: agentId,
        resource_ids: resourceIds,
        kind: "folder",
        resource_labels: resourceLabels,
      });
      onAttached?.(selected.size);
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to attach";
      setError(msg);
    } finally {
      setAttaching(false);
    }
  }, [agentId, integrationId, selected, onAttached, onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-[var(--surface-container)] border border-[var(--outline-variant)] rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-[var(--outline-variant)] flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-[var(--on-surface)]">
              Attach Drive folders
            </h2>
            {agentName && (
              <p className="text-xs text-[var(--on-surface-variant)] mt-0.5">
                to <span className="font-medium text-[var(--on-surface)]">{agentName}</span>
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-[var(--surface-high)] transition-colors"
            aria-label="Close"
          >
            <X className="size-4 text-[var(--on-surface-variant)]" />
          </button>
        </div>

        {/* Breadcrumbs */}
        <div className="px-5 py-2 border-b border-[var(--outline-variant)] flex items-center gap-1 text-xs overflow-x-auto">
          {breadcrumbs.map((bc, i) => (
            <span key={bc.id} className="flex items-center gap-1 shrink-0">
              {i > 0 && <ChevronRight className="size-3 text-[var(--on-surface-muted)]" />}
              <button
                type="button"
                onClick={() => navTo(i)}
                className={
                  i === breadcrumbs.length - 1
                    ? "font-semibold text-[var(--on-surface)]"
                    : "text-[var(--primary)] hover:underline"
                }
              >
                {bc.name}
              </button>
            </span>
          ))}
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-2 py-2 min-h-[200px]">
          {loading && (
            <div className="flex items-center justify-center py-12 text-xs text-[var(--on-surface-variant)]">
              Loading…
            </div>
          )}
          {!loading && error && (
            <div className="mx-3 my-4 px-3 py-2 rounded-lg bg-[var(--error-container)] text-xs text-[var(--on-error-container)]">
              {error}
            </div>
          )}
          {!loading && !error && items.length === 0 && (
            <div className="flex items-center justify-center py-12 text-xs text-[var(--on-surface-variant)]">
              (empty folder)
            </div>
          )}
          {!loading && !error && items.map((item) => {
            const isSelected = selected.has(item.id);
            return (
              <div
                key={item.id}
                className={[
                  "flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors",
                  isSelected
                    ? "bg-[var(--primary-container)]"
                    : "hover:bg-[var(--surface-high)]",
                ].join(" ")}
                onClick={() => (item.is_folder ? toggleSelect(item) : undefined)}
                onDoubleClick={() => openFolder(item)}
              >
                {item.is_folder ? (
                  <Folder className="size-4 text-[var(--primary)]" />
                ) : (
                  <FileIcon className="size-4 text-[var(--on-surface-muted)]" />
                )}
                <span className={`flex-1 text-xs ${item.is_folder ? "text-[var(--on-surface)] font-medium" : "text-[var(--on-surface-variant)]"}`}>
                  {item.name}
                </span>
                {item.is_folder && (
                  <>
                    {isSelected && <Check className="size-4 text-[var(--primary)]" />}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openFolder(item);
                      }}
                      className="text-[10px] text-[var(--primary)] hover:underline"
                    >
                      Open
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-[var(--outline-variant)] flex items-center justify-between">
          <p className="text-xs text-[var(--on-surface-variant)]">
            {selected.size === 0 ? "Select folders to attach" : `${selected.size} selected`}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 rounded-lg text-xs text-[var(--on-surface-variant)] hover:bg-[var(--surface-high)] transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={selected.size === 0 || attaching}
              onClick={handleAttach}
              className="px-4 py-1.5 rounded-lg text-xs font-medium bg-[var(--primary)] text-[var(--on-primary)] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              {attaching ? "Attaching…" : `Attach${selected.size > 0 ? ` (${selected.size})` : ""}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
